import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { http, money, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Edit3, Layers, FileUp } from "lucide-react";
import { toast } from "sonner";
import { useReportLink, MonthChargesTable } from "@/components/ReportLink";
import CompensiOperatori from "@/components/CompensiOperatori";

const empty = () => ({
  date: new Date().toISOString().slice(0, 10), property_id: "", apartment_id: "",
  operator1_name: "", operator1_fee: 0, operator2_name: "", operator2_fee: 0,
  total_cost: 0, guests: 0, paid: false, notes: "",
  charged_to: "proprietario", considera_rendiconto: true, mostra_pdf: true,
});

const emptyRow = (tariff, suggested) => ({
  operator1_name: suggested || "", operator1_fee: tariff, operator2_name: "", operator2_fee: 0,
  two: false, total_cost: tariff, paid: false, notes: "",
});

// Suggerimento (mai vincolante): Michela lavora di norma su Sistiana
const suggestOperator = (aptName, cleaners) => {
  if (!aptName) return "";
  if (!/sistiana/i.test(aptName)) return "";
  return cleaners.some((c) => c.name === "Michela" && c.active) ? "Michela" : "";
};

function CleanerSelect({ value, onChange, cleaners, testid, placeholder = "Seleziona operatrice" }) {
  const known = cleaners.some((c) => c.name === value);
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {cleaners.filter((c) => c.active).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
        {value && !known && <SelectItem value={value}>{value} (storico)</SelectItem>}
      </SelectContent>
    </Select>
  );
}

export default function Pulizie() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const { propose, dialog: linkDialog } = useReportLink();
  const [form, setForm] = useState(empty());
  const [editId, setEditId] = useState(null);
  const [twoOps, setTwoOps] = useState(false);
  // inserimento multiplo
  const [multiOpen, setMultiOpen] = useState(false);
  const [mDate, setMDate] = useState(new Date().toISOString().slice(0, 10));
  const [mRows, setMRows] = useState({});
  const [savingMulti, setSavingMulti] = useState(false);
  const now = new Date();
  const [fMonth, setFMonth] = useState(String(now.getMonth() + 1));
  const [fYear, setFYear] = useState(String(now.getFullYear()));
  const [fApt, setFApt] = useState("all");
  const [fOperator, setFOperator] = useState("");
  const [fPaid, setFPaid] = useState("all");

  const load = async () => {
    const [cl, apt, cle] = await Promise.all([http.get("/cleanings"), http.get("/apartments"), http.get("/cleaners")]);
    setItems(cl.data); setApartments(apt.data); setCleaners(cle.data);
  };
  const loadSummary = async () => {
    const p = {};
    if (fMonth !== "all") p.month = Number(fMonth);
    if (fYear !== "all") p.year = Number(fYear);
    if (fApt !== "all") p.apartment_id = fApt;
    if (fOperator) p.operator = fOperator;
    if (fPaid !== "all") p.paid = fPaid === "si";
    const { data } = await http.get("/cleanings-summary", { params: p });
    setSummary(data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadSummary(); }, [fMonth, fYear, fApt, fOperator, fPaid, items]);

  useEffect(() => {
    if (params.get("new") === "1") { setForm(empty()); setEditId(null); setTwoOps(false); setOpen(true); params.delete("new"); setParams(params, { replace: true }); }
    if (params.get("new") === "multi") { setMRows({}); setMDate(new Date().toISOString().slice(0, 10)); setMultiOpen(true); params.delete("new"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const applyAptTariff = (aptId, f) => {
    const a = apartments.find((x) => x.id === aptId);
    const total = a?.cleaning_cost ?? f.total_cost;
    const sug = suggestOperator(a?.name, cleaners);
    return { ...f, apartment_id: aptId, property_id: a?.property_id || f.property_id,
             operator1_name: f.operator1_name || sug,
             total_cost: total, operator1_fee: twoOps ? total / 2 : total, operator2_fee: twoOps ? total / 2 : 0 };
  };
  const onAptChange = (v) => setForm((f) => applyAptTariff(v, f));
  const onTotalChange = (v) => {
    const t = Number(v) || 0;
    setForm((f) => ({ ...f, total_cost: v, operator1_fee: twoOps ? t / 2 : t, operator2_fee: twoOps ? t / 2 : 0 }));
  };
  const onToggleTwo = (v) => {
    setTwoOps(v);
    const t = Number(form.total_cost) || 0;
    setForm((f) => ({ ...f, operator1_fee: v ? t / 2 : t, operator2_fee: v ? t / 2 : 0, operator2_name: v ? f.operator2_name : "" }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form,
      operator1_fee: Number(form.operator1_fee) || 0, operator2_fee: Number(form.operator2_fee) || 0,
      total_cost: Number(form.total_cost) || 0, guests: Number(form.guests) || 0 };
    ["property_id", "apartment_id"].forEach((k) => { if (!payload[k]) payload[k] = null; });
    if (!payload.operator2_name) { payload.operator2_name = null; payload.operator2_fee = 0; }
    try {
      let saved;
      if (editId) { saved = (await http.put(`/cleanings/${editId}`, payload)).data; toast.success("Pulizia aggiornata"); }
      else { saved = (await http.post("/cleanings", payload)).data; toast.success("Pulizia registrata"); }
      setOpen(false); load();
      if (saved.apartment_id && saved.date) {
        const d = new Date(saved.date);
        propose({ apartment_id: saved.apartment_id, year: d.getFullYear(), month: d.getMonth() + 1, category: "pulizie", use_operative: true, label: "Suggerito dal registro Pulizie" }, { soloLabel: "Solo Pulizie", silentIfNoReport: true });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onDelete = async (id) => { if (!window.confirm("Eliminare la pulizia?")) return; await http.delete(`/cleanings/${id}`); toast.success("Eliminata"); load(); };
  const onEdit = (c) => { setForm({ ...empty(), ...c }); setEditId(c.id); setTwoOps(!!c.operator2_name); setOpen(true); };

  // ---------- Inserimento multiplo ----------
  const toggleApt = (apt) => {
    setMRows((rows) => {
      const next = { ...rows };
      if (next[apt.id]) delete next[apt.id];
      else next[apt.id] = emptyRow(Number(apt.cleaning_cost) || 0, suggestOperator(apt.name, cleaners));
      return next;
    });
  };
  const updRow = (aptId, patch) => setMRows((rows) => ({ ...rows, [aptId]: { ...rows[aptId], ...patch } }));
  const updRowTotal = (aptId, v) => {
    const t = Number(v) || 0;
    setMRows((rows) => {
      const r = rows[aptId];
      return { ...rows, [aptId]: { ...r, total_cost: v, operator1_fee: r.two ? t / 2 : t, operator2_fee: r.two ? t / 2 : 0 } };
    });
  };
  const updRowTwo = (aptId, v) => {
    setMRows((rows) => {
      const r = rows[aptId];
      const t = Number(r.total_cost) || 0;
      return { ...rows, [aptId]: { ...r, two: v, operator1_fee: v ? t / 2 : t, operator2_fee: v ? t / 2 : 0, operator2_name: v ? r.operator2_name : "" } };
    });
  };
  const saveMulti = async () => {
    const entries = Object.entries(mRows);
    if (!entries.length) return toast.error("Seleziona almeno un appartamento");
    setSavingMulti(true);
    let okCount = 0, failCount = 0;
    for (const [aptId, row] of entries) {
      const a = apartments.find((x) => x.id === aptId);
      const payload = {
        date: mDate, apartment_id: aptId, property_id: a?.property_id || null,
        operator1_name: row.operator1_name || null, operator1_fee: Number(row.operator1_fee) || 0,
        operator2_name: row.two && row.operator2_name ? row.operator2_name : null,
        operator2_fee: row.two ? Number(row.operator2_fee) || 0 : 0,
        total_cost: Number(row.total_cost) || 0, guests: 0, paid: !!row.paid, notes: row.notes || null,
        charged_to: "proprietario", considera_rendiconto: true, mostra_pdf: true,
      };
      try { await http.post("/cleanings", payload); okCount++; }
      catch { failCount++; }
    }
    setSavingMulti(false);
    if (failCount) toast.error(`${okCount} create, ${failCount} fallite`);
    else toast.success(`${okCount} pulizie registrate per il ${fmtDate(mDate)}`);
    setMultiOpen(false); setMRows({}); load();
  };

  const aptName = (id) => apartments.find((a) => a.id === id)?.name || "-";
  const filtered = items.filter((i) => {
    if (fMonth !== "all" || fYear !== "all") {
      const [y, m] = i.date.split("-");
      if (fYear !== "all" && y !== fYear) return false;
      if (fMonth !== "all" && Number(m) !== Number(fMonth)) return false;
    }
    if (fApt !== "all" && i.apartment_id !== fApt) return false;
    if (fPaid !== "all" && i.paid !== (fPaid === "si")) return false;
    if (fOperator) {
      const names = [i.operator1_name, i.operator2_name].filter(Boolean).join(" ").toLowerCase();
      if (!names.includes(fOperator.toLowerCase())) return false;
    }
    return true;
  });
  const multiSelected = Object.keys(mRows).length;

  return (
    <div className="space-y-6" data-testid="pulizie-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--terracotta)] font-semibold">Pulizie</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--teal-deep)]">Registro operativo</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" data-testid="import-month-btn" onClick={() => navigate("/pulizie/importazione")} className="rounded-xl min-h-[44px]"><FileUp className="h-4 w-4 mr-1" /> Importazione mensile</Button>
          <Button variant="outline" data-testid="new-multi-cleaning-btn" onClick={() => { setMRows({}); setMDate(new Date().toISOString().slice(0, 10)); setMultiOpen(true); }} className="rounded-xl min-h-[44px]"><Layers className="h-4 w-4 mr-1" /> Inserimento multiplo</Button>
          <Button data-testid="new-cleaning-btn" onClick={() => { setForm(empty()); setEditId(null); setTwoOps(false); setOpen(true); }} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl min-h-[44px]"><Plus className="h-4 w-4 mr-1" /> Nuova pulizia</Button>
        </div>
      </div>

      <Card className="p-4 rounded-2xl">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div><Label>Mese</Label>
            <Select value={fMonth} onValueChange={setFMonth}><SelectTrigger data-testid="f-month"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem>{["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"].map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Anno</Label>
            <Select value={fYear} onValueChange={setFYear}><SelectTrigger data-testid="f-year"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Appartamento</Label>
            <Select value={fApt} onValueChange={setFApt}><SelectTrigger data-testid="f-apt"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Operatrice</Label><Input placeholder="Nome" value={fOperator} onChange={(e) => setFOperator(e.target.value)} data-testid="f-operator" /></div>
          <div><Label>Pagato</Label>
            <Select value={fPaid} onValueChange={setFPaid}><SelectTrigger data-testid="f-paid"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="si">Pagato</SelectItem><SelectItem value="no">Non pagato</SelectItem></SelectContent></Select>
          </div>
        </div>
      </Card>


      <MonthChargesTable category="pulizie" testid="charges-pulizie" />

      <CompensiOperatori year={fYear !== "all" ? Number(fYear) : null} month={fMonth !== "all" ? Number(fMonth) : null} />

      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-4 rounded-2xl" data-testid="sum-apartment">
            <div className="font-display font-bold text-[var(--teal-deep)] mb-2">Per appartamento</div>
            <div className="space-y-1.5 text-sm">
              {summary.per_apartment.length === 0 && <div className="text-slate-400">Nessun dato</div>}
              {summary.per_apartment.map((p) => (
                <div key={p.apartment_id} className="flex justify-between"><span>{p.apartment_name} <span className="text-slate-400">({p.count})</span></span><span className="font-semibold tabular-nums">{money(p.total)}</span></div>
              ))}
            </div>
          </Card>
          <Card className="p-4 rounded-2xl" data-testid="sum-operator">
            <div className="font-display font-bold text-[var(--teal-deep)] mb-2">Per operatrice</div>
            <div className="space-y-1.5 text-sm">
              {summary.per_operator.length === 0 && <div className="text-slate-400">Nessun dato</div>}
              {summary.per_operator.map((p) => (
                <div key={p.operator} className="flex justify-between gap-2">
                  <span>{p.operator} <span className="text-slate-400">({p.count})</span></span>
                  <span className="tabular-nums"><b>{money(p.total)}</b>{p.unpaid > 0 && <span className="text-amber-700"> · da pagare {money(p.unpaid)}</span>}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-4 rounded-2xl" data-testid="sum-operator-apartment">
            <div className="font-display font-bold text-[var(--teal-deep)] mb-2">Dettaglio operatrice × appartamento</div>
            <div className="space-y-1.5 text-sm max-h-48 overflow-y-auto">
              {summary.per_operator_apartment.length === 0 && <div className="text-slate-400">Nessun dato</div>}
              {summary.per_operator_apartment.map((p, i) => (
                <div key={i} className="flex justify-between gap-2 text-xs"><span>{p.operator} · {p.apartment_name} <span className="text-slate-400">({p.count})</span></span><span className="font-semibold tabular-nums">{money(p.total)}</span></div>
              ))}
            </div>
          </Card>
        </div>
      )}

      <Card className="rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cleanings-table">
            <thead><tr className="text-left border-b border-slate-200 text-xs uppercase text-slate-500">
              <th className="py-2 px-4">Data</th><th className="px-2">Appartamento</th><th className="px-2">Operatrice 1</th><th className="px-2 text-right">Compenso</th>
              <th className="px-2">Operatrice 2</th><th className="px-2 text-right">Compenso</th><th className="px-2 text-right">Totale</th><th className="px-2">Pagato</th><th className="px-2"></th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={9} className="py-10 text-center text-slate-400">Nessuna pulizia nel periodo.</td></tr>}
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cleaning-${c.id}`}>
                  <td className="py-3 px-4 tabular-nums whitespace-nowrap">{fmtDate(c.date)}</td>
                  <td className="px-2">{aptName(c.apartment_id)}</td>
                  <td className="px-2">{c.operator1_name || "-"}</td>
                  <td className="px-2 text-right tabular-nums">{money(c.operator1_fee)}</td>
                  <td className="px-2">{c.operator2_name || "-"}</td>
                  <td className="px-2 text-right tabular-nums">{c.operator2_name ? money(c.operator2_fee) : "-"}</td>
                  <td className="px-2 text-right tabular-nums font-semibold">{money(c.total_cost)}</td>
                  <td className="px-2">{c.paid ? <Badge className="bg-emerald-100 text-emerald-800">Pagato</Badge> : <Badge variant="outline" className="border-amber-300 text-amber-800">No</Badge>}</td>
                  <td className="px-2"><div className="flex gap-1 justify-end">
                    <button onClick={() => onEdit(c)} data-testid={`edit-cleaning-${c.id}`} className="p-1.5 text-slate-500 hover:text-teal-700"><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => onDelete(c.id)} className="p-1.5 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Dialog singola pulizia */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Modifica" : "Nuova"} pulizia</DialogTitle></DialogHeader>
          <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="cleaning-form">
            <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="cleaning-date" /></div>
            <div><Label>Appartamento</Label>
              <Select value={form.apartment_id || ""} onValueChange={onAptChange}><SelectTrigger data-testid="cleaning-apt"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} {a.cleaning_cost ? `(€${a.cleaning_cost})` : ""}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Totale costo pulizia (€)</Label><Input type="number" step="0.01" value={form.total_cost} onChange={(e) => onTotalChange(e.target.value)} data-testid="cleaning-total" /></div>
            <div className="flex items-center gap-3 pt-6"><Switch checked={twoOps} onCheckedChange={onToggleTwo} data-testid="cleaning-two-ops" /><Label>Due operatrici (divisione equa)</Label></div>
            <div><Label>Operatrice 1</Label><CleanerSelect value={form.operator1_name} onChange={(v) => setForm({ ...form, operator1_name: v })} cleaners={cleaners} testid="cleaning-op1" /></div>
            <div><Label>Compenso operatrice 1 (€)</Label><Input type="number" step="0.01" value={form.operator1_fee} onChange={(e) => setForm({ ...form, operator1_fee: e.target.value })} data-testid="cleaning-fee1" /></div>
            {twoOps && <>
              <div><Label>Operatrice 2</Label><CleanerSelect value={form.operator2_name} onChange={(v) => setForm({ ...form, operator2_name: v })} cleaners={cleaners} testid="cleaning-op2" /></div>
              <div><Label>Compenso operatrice 2 (€)</Label><Input type="number" step="0.01" value={form.operator2_fee} onChange={(e) => setForm({ ...form, operator2_fee: e.target.value })} data-testid="cleaning-fee2" /></div>
            </>}
            <div><Label>Ospiti</Label><Input type="number" min="0" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} /></div>
            <div className="flex items-center gap-3 pt-6"><Switch checked={form.paid} onCheckedChange={(v) => setForm({ ...form, paid: v })} data-testid="cleaning-paid" /><Label>Pagato</Label></div>
            <div><Label>A carico di</Label>
              <Select value={form.charged_to} onValueChange={(v) => setForm({ ...form, charged_to: v })}><SelectTrigger data-testid="cleaning-charged"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="proprietario">Proprietario</SelectItem><SelectItem value="puravida">PuraVida Home</SelectItem></SelectContent></Select>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <div className="flex items-center gap-3"><Switch checked={form.considera_rendiconto} onCheckedChange={(v) => setForm({ ...form, considera_rendiconto: v })} data-testid="cleaning-considera" /><Label className="text-xs">Considera nel rendiconto</Label></div>
              <div className="flex items-center gap-3"><Switch checked={form.mostra_pdf} onCheckedChange={(v) => setForm({ ...form, mostra_pdf: v })} data-testid="cleaning-showpdf" /><Label className="text-xs">Mostra nel PDF</Label></div>
            </div>
            <div className="md:col-span-2"><Label>Note</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="cleaning-submit">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog inserimento multiplo */}
      <Dialog open={multiOpen} onOpenChange={setMultiOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="multi-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Inserimento multiplo — pulizie della giornata</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="max-w-xs"><Label>Data (condivisa)</Label><Input type="date" value={mDate} onChange={(e) => setMDate(e.target.value)} data-testid="multi-date" /></div>
            <div className="text-sm text-slate-500">Seleziona uno o più appartamenti e compila i dati di ciascuno. Verrà creato un record indipendente per ogni appartamento.</div>
            <div className="space-y-3">
              {apartments.map((a) => {
                const sel = mRows[a.id];
                return (
                  <div key={a.id} className={`rounded-2xl border p-4 transition ${sel ? "border-[var(--teal-deep)] bg-teal-50/40" : "border-slate-200"}`} data-testid={`multi-apt-${a.id}`}>
                    <button type="button" onClick={() => toggleApt(a)} data-testid={`multi-toggle-${a.id}`}
                      className="w-full flex items-center justify-between min-h-[44px]">
                      <span className="font-semibold text-slate-900">{a.name} <span className="text-xs text-slate-500 font-normal">tariffa {money(a.cleaning_cost)}</span></span>
                      <span className={`h-6 w-6 rounded-md border-2 flex items-center justify-center text-xs font-bold ${sel ? "bg-[var(--teal-deep)] border-[var(--teal-deep)] text-white" : "border-slate-300 text-transparent"}`}>✓</span>
                    </button>
                    {sel && (
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div><Label className="text-xs">Totale (€)</Label><Input type="number" step="0.01" value={sel.total_cost} onChange={(e) => updRowTotal(a.id, e.target.value)} data-testid={`multi-total-${a.id}`} /></div>
                        <div><Label className="text-xs">Operatrice 1</Label><CleanerSelect value={sel.operator1_name} onChange={(v) => updRow(a.id, { operator1_name: v })} cleaners={cleaners} testid={`multi-op1-${a.id}`} /></div>
                        <div><Label className="text-xs">Compenso 1 (€)</Label><Input type="number" step="0.01" value={sel.operator1_fee} onChange={(e) => updRow(a.id, { operator1_fee: e.target.value })} data-testid={`multi-fee1-${a.id}`} /></div>
                        <div className="flex items-end gap-2 pb-1"><Switch checked={sel.two} onCheckedChange={(v) => updRowTwo(a.id, v)} data-testid={`multi-two-${a.id}`} /><Label className="text-xs">2 operatrici</Label></div>
                        {sel.two && <>
                          <div><Label className="text-xs">Operatrice 2</Label><CleanerSelect value={sel.operator2_name} onChange={(v) => updRow(a.id, { operator2_name: v })} cleaners={cleaners} testid={`multi-op2-${a.id}`} /></div>
                          <div><Label className="text-xs">Compenso 2 (€)</Label><Input type="number" step="0.01" value={sel.operator2_fee} onChange={(e) => updRow(a.id, { operator2_fee: e.target.value })} data-testid={`multi-fee2-${a.id}`} /></div>
                        </>}
                        <div className="flex items-end gap-2 pb-1"><Switch checked={sel.paid} onCheckedChange={(v) => updRow(a.id, { paid: v })} data-testid={`multi-paid-${a.id}`} /><Label className="text-xs">Pagato</Label></div>
                        <div className="col-span-2 md:col-span-3"><Label className="text-xs">Note</Label><Input value={sel.notes} onChange={(e) => updRow(a.id, { notes: e.target.value })} data-testid={`multi-notes-${a.id}`} /></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMultiOpen(false)}>Annulla</Button>
              <Button onClick={saveMulti} disabled={savingMulti || !multiSelected} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] min-h-[44px]" data-testid="multi-save">
                {savingMulti ? "Salvataggio..." : `Salva ${multiSelected || ""} pulizie`}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
      {linkDialog}
    </div>
  );
}
